import React, { useCallback, useEffect, useState } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useClub } from "../contexts/ClubContext";
import { ROLES, roleRank, roleLabel, assignableRoles, canManageAccount, isCreator } from "../lib/roles";
import { BF, FF } from "../lib/constants";
import BackBtn from "../components/BackBtn";
import Modal from "../components/Modal";
import { RolesList } from "../components/AccountMenu";

var inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 14, outline: "none", marginBottom: 10, fontFamily: FF, boxSizing: "border-box", background: "#fff" };

var fnCreate = httpsCallable(functions, "createAccount");
var fnRole = httpsCallable(functions, "updateRole");
var fnName = httpsCallable(functions, "updateName");
var fnLink = httpsCallable(functions, "passwordLink");
var fnDelete = httpsCallable(functions, "deleteAccount");
var fnApprove = httpsCallable(functions, "approveClub");
var fnReject = httpsCallable(functions, "rejectClub");

// Zelfde omzetting als in functions/index.js (slugify), voor het voorstel in het veld.
function slugify(v) {
  return String(v || "").toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
}

// Eén openstaande club-aanmelding, met aanpasbare clubnaam en webadres.
function RequestCard({ req, busy, onApprove, onReject, mob }) {
  var [name, setName] = useState(req.clubName || "");
  var [slug, setSlug] = useState(slugify(req.clubName));
  var [slugTouched, setSlugTouched] = useState(false);
  var when = req.createdAt && req.createdAt.toDate ? req.createdAt.toDate().toLocaleDateString("nl-NL", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }) : "";
  var small = { padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13, fontFamily: FF, outline: "none", boxSizing: "border-box", width: "100%" };
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: 16, border: "2px solid #FBD38D", opacity: busy ? .6 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={{ fontFamily: BF, fontSize: 16, color: "#1C1B19", fontWeight: 800 }}>{req.clubName}{req.place ? <span style={{ fontFamily: FF, fontSize: 12, color: "#888", letterSpacing: 0 }}> · {req.place}</span> : null}</div>
          <div style={{ fontSize: 13, color: "#555" }}>{req.name} · <a href={"mailto:" + req.email} style={{ color: "#555" }}>{req.email}</a></div>
        </div>
        {when && <div style={{ fontSize: 11, color: "#999" }}>{when}</div>}
      </div>
      <div style={{ display: "flex", gap: 8, flexDirection: mob ? "column" : "row", marginBottom: 10 }}>
        <label style={{ flex: 1, fontSize: 11, color: "#888" }}>Clubnaam
          <input value={name} onChange={function (e) { setName(e.target.value); if (!slugTouched) setSlug(slugify(e.target.value)); }} style={small} />
        </label>
        <label style={{ flex: 1, fontSize: 11, color: "#888" }}>Webadres
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#999", whiteSpace: "nowrap" }}>…web.app/</span>
            <input value={slug} onChange={function (e) { setSlugTouched(true); setSlug(slugify(e.target.value)); }} style={small} />
          </div>
        </label>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button disabled={busy} onClick={function () { onReject(req); }} style={{ flex: 1, padding: 9, borderRadius: 8, background: "#fff", color: "#E53E3E", border: "1px solid #FEB2B2", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>Afwijzen</button>
        <button disabled={busy || slug.length < 2 || name.trim().length < 2} onClick={function () { onApprove(req, name.trim(), slug); }} style={{ flex: 2, padding: 9, borderRadius: 8, background: "#38A169", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>✓ Goedkeuren als hoofdtrainer</button>
      </div>
    </div>
  );
}

function errText(e) {
  // HttpsError-meldingen uit de Cloud Functions zijn al Nederlandstalig.
  if (e && e.message && e.code && String(e.code).indexOf("functions/") === 0 && e.code !== "functions/internal") return e.message;
  return "Er ging iets mis (" + ((e && (e.code || e.message)) || "onbekend") + ").";
}

function LinkBox({ link }) {
  var [copied, setCopied] = useState(false);
  function copy() {
    if (navigator.clipboard) navigator.clipboard.writeText(link).then(function () { setCopied(true); setTimeout(function () { setCopied(false); }, 1500); });
  }
  var wa = "https://wa.me/?text=" + encodeURIComponent("Je account voor Seizoensplan staat klaar. Stel via deze link je wachtwoord in (link is 1 uur geldig): " + link);
  return (
    <div>
      <textarea readOnly value={link} rows={3} onFocus={function (e) { e.target.select(); }} style={Object.assign({}, inputStyle, { fontSize: 11, color: "#555", resize: "none" })} />
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={copy} style={{ flex: 1, padding: 10, borderRadius: 8, background: "#1A2744", color: "#fff", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{copied ? "Gekopieerd ✓" : "Link kopiëren"}</button>
        <a href={wa} target="_blank" rel="noreferrer" style={{ flex: 1, padding: 10, borderRadius: 8, background: "#25D366", color: "#fff", textDecoration: "none", textAlign: "center", fontSize: 13, fontWeight: 700 }}>WhatsApp</a>
      </div>
    </div>
  );
}

export default function Accounts({ mob, pd, onBack }) {
  var { profile, resetPassword } = useAuth();
  var { clubId, club } = useClub();
  var [users, setUsers] = useState(null);
  var [clubs, setClubs] = useState({}); // clubId -> naam (alleen voor creator)
  var [clubSlugs, setClubSlugs] = useState({}); // clubId -> slug (alleen voor creator)
  var [requests, setRequests] = useState([]); // openstaande club-aanmeldingen (creator)
  var [reqBusy, setReqBusy] = useState(null);
  var [reqErr, setReqErr] = useState("");
  var [openClubs, setOpenClubs] = useState(function () { var o = {}; o[clubId] = true; return o; });
  var [newClub, setNewClub] = useState(clubId);
  var [loadErr, setLoadErr] = useState("");
  var [busyUid, setBusyUid] = useState(null);
  var [rowErr, setRowErr] = useState("");

  var [name, setName] = useState("");
  var [email, setEmail] = useState("");
  var [role, setRole] = useState("trainer");
  var [creating, setCreating] = useState(false);
  var [createErr, setCreateErr] = useState("");

  var [mdl, setMdl] = useState(null); // { type: "created"|"link"|"delete"|"name", user, link, mailSent }
  var [editName, setEditName] = useState("");

  var creator = isCreator(profile);
  var roleOptions = assignableRoles(profile.role);

  var load = useCallback(async function () {
    setLoadErr("");
    try {
      // Een creator ziet alle accounts van alle clubs (in mapjes per club), anderen
      // alleen die van hun eigen club.
      if (creator) {
        var cs = await getDocs(collection(db, "clubs"));
        var cm = {}, sm = {};
        cs.docs.forEach(function (d) { cm[d.id] = d.data().name || d.data().slug || d.id; sm[d.id] = d.data().slug || d.id; });
        setClubs(cm); setClubSlugs(sm);
        var rq = await getDocs(query(collection(db, "clubRequests"), where("status", "==", "pending")));
        var rl = rq.docs.map(function (d) { return d.data(); });
        rl.sort(function (a, b) { return ((a.createdAt && a.createdAt.seconds) || 0) - ((b.createdAt && b.createdAt.seconds) || 0); });
        setRequests(rl);
      }
      var snap = await getDocs(creator ? collection(db, "users") : query(collection(db, "users"), where("clubId", "==", clubId)));
      // Aanmeldingen die nog op goedkeuring wachten staan bij "Aanvragen", niet in de lijst.
      var list = snap.docs.map(function (d) { return Object.assign({ uid: d.id }, d.data()); })
        .filter(function (u) { return u.status !== "pending"; });
      list.sort(function (a, b) {
        var r = roleRank(b.role) - roleRank(a.role);
        if (r) return r;
        return (a.name || a.email).localeCompare(b.name || b.email);
      });
      setUsers(list);
    } catch (e) {
      console.error(e);
      setLoadErr("Accounts laden mislukt (" + (e.code || e.message) + ").");
      setUsers([]);
    }
  }, [clubId, creator]);
  useEffect(function () { load(); }, [load]);

  function canManage(u) {
    return canManageAccount(profile, u);
  }

  async function create(e) {
    e.preventDefault();
    setCreateErr(""); setCreating(true);
    try {
      var targetClub = creator ? newClub : clubId;
      var newRole = creator ? role : "trainer";
      var res = await fnCreate({ email: email.trim(), name: name.trim(), role: newRole, clubId: targetClub });
      var mailSent = true;
      try { await resetPassword(email.trim()); } catch (e2) { console.error(e2); mailSent = false; }
      setMdl({ type: "created", user: { name: name.trim(), email: email.trim(), role: newRole }, link: res.data.link, mailSent: mailSent });
      if (creator) setOpenClubs(function (o) { var n = Object.assign({}, o); n[targetClub] = true; return n; });
      setName(""); setEmail(""); setRole("trainer");
      load();
    } catch (e2) {
      console.error(e2);
      setCreateErr(errText(e2));
    }
    setCreating(false);
  }

  async function changeRole(u, newRole) {
    if (newRole === u.role) return;
    setRowErr(""); setBusyUid(u.uid);
    try { await fnRole({ uid: u.uid, role: newRole }); await load(); }
    catch (e) { console.error(e); setRowErr(errText(e)); }
    setBusyUid(null);
  }

  async function newLink(u) {
    setRowErr(""); setBusyUid(u.uid);
    try {
      var res = await fnLink({ uid: u.uid });
      var mailSent = true;
      try { await resetPassword(u.email); } catch (e2) { console.error(e2); mailSent = false; }
      setMdl({ type: "link", user: u, link: res.data.link, mailSent: mailSent });
    } catch (e) { console.error(e); setRowErr(errText(e)); }
    setBusyUid(null);
  }

  async function doDelete(u) {
    setRowErr(""); setBusyUid(u.uid); setMdl(null);
    try { await fnDelete({ uid: u.uid }); await load(); }
    catch (e) { console.error(e); setRowErr(errText(e)); }
    setBusyUid(null);
  }

  async function saveName(u) {
    setRowErr(""); setBusyUid(u.uid); setMdl(null);
    try { await fnName({ uid: u.uid, name: editName.trim() }); await load(); }
    catch (e) { console.error(e); setRowErr(errText(e)); }
    setBusyUid(null);
  }

  async function approve(req, name2, slug) {
    setReqErr(""); setReqBusy(req.uid);
    try {
      var res = await fnApprove({ uid: req.uid, clubName: name2, slug: slug });
      setOpenClubs(function (o) { var n = Object.assign({}, o); n[res.data.clubId] = true; return n; });
      await load();
    } catch (e) { console.error(e); setReqErr(errText(e)); }
    setReqBusy(null);
  }

  async function reject(req) {
    setReqErr(""); setReqBusy(req.uid); setMdl(null);
    try { await fnReject({ uid: req.uid }); await load(); }
    catch (e) { console.error(e); setReqErr(errText(e)); }
    setReqBusy(null);
  }

  function renderModal() {
    if (!mdl) return null;
    var close = function () { setMdl(null); };
    if (mdl.type === "created" || mdl.type === "link") return (
      <Modal onClose={close}>
        <h3 style={{ fontFamily: BF, fontSize: 18, marginBottom: 10, color: "#1C1B19", fontWeight: 800 }}>{mdl.type === "created" ? "Account aangemaakt ✓" : "Nieuwe wachtwoordlink"}</h3>
        <p style={{ fontSize: 13, color: "#555", marginBottom: 12, lineHeight: 1.6 }}>
          {mdl.mailSent
            ? <span>Er is een mail gestuurd naar <b>{mdl.user.email}</b> met een link om een wachtwoord in te stellen.</span>
            : <span>De mail naar <b>{mdl.user.email}</b> kon niet verstuurd worden.</span>}
          {" "}Je kunt de link hieronder ook zelf doorsturen. De link is <b>1 uur</b> geldig. Verlopen? Dan kan {mdl.user.name || "diegene"} op het loginscherm "Wachtwoord vergeten?" gebruiken.
        </p>
        <LinkBox link={mdl.link} />
      </Modal>
    );
    if (mdl.type === "reject") return (
      <Modal onClose={close}>
        <h3 style={{ fontFamily: BF, fontSize: 18, color: "#E53E3E", marginBottom: 10, fontWeight: 800 }}>Aanmelding afwijzen?</h3>
        <p style={{ fontSize: 13, color: "#555", marginBottom: 14, lineHeight: 1.6 }}>Het account van {mdl.req.name} ({mdl.req.email}) wordt verwijderd en {mdl.req.name.split(" ")[0]} krijgt een mail dat de aanmelding niet is goedgekeurd.</p>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={close} style={{ flex: 1, padding: 10, borderRadius: 8, background: "#f0f0f0", border: "none", cursor: "pointer", fontWeight: 700, color: "#555" }}>Annuleren</button>
          <button onClick={function () { reject(mdl.req); }} style={{ flex: 1, padding: 10, borderRadius: 8, background: "#E53E3E", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700 }}>Afwijzen</button>
        </div>
      </Modal>
    );
    if (mdl.type === "delete") return (
      <Modal onClose={close}>
        <h3 style={{ fontFamily: BF, fontSize: 18, color: "#E53E3E", marginBottom: 10, fontWeight: 800 }}>Account verwijderen?</h3>
        <p style={{ fontSize: 13, color: "#555", marginBottom: 14 }}>{mdl.user.name || mdl.user.email} kan daarna niet meer inloggen. Dit kan niet ongedaan worden gemaakt.</p>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={close} style={{ flex: 1, padding: 10, borderRadius: 8, background: "#f0f0f0", border: "none", cursor: "pointer", fontWeight: 700, color: "#555" }}>Annuleren</button>
          <button onClick={function () { doDelete(mdl.user); }} style={{ flex: 1, padding: 10, borderRadius: 8, background: "#E53E3E", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700 }}>Verwijderen</button>
        </div>
      </Modal>
    );
    if (mdl.type === "name") return (
      <Modal onClose={close}>
        <h3 style={{ fontFamily: BF, fontSize: 18, marginBottom: 14, color: "#1C1B19", fontWeight: 800 }}>Naam aanpassen</h3>
        <input value={editName} onChange={function (e) { setEditName(e.target.value); }} onKeyDown={function (e) { if (e.key === "Enter") saveName(mdl.user); }} placeholder="Naam" autoFocus style={inputStyle} />
        <button onClick={function () { saveName(mdl.user); }} style={{ width: "100%", padding: 10, borderRadius: 8, background: "#1A2744", color: "#fff", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>Opslaan</button>
      </Modal>
    );
    return null;
  }

  function renderRow(u) {
          var manageable = canManage(u);
          var busy = busyUid === u.uid;
          var isMe = u.uid === profile.uid;
          return (
            <div key={u.uid} style={{ background: "#fff", borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: mob ? "stretch" : "center", gap: 12, flexDirection: mob ? "column" : "row", opacity: busy ? .6 : 1, boxShadow: "0 1px 6px rgba(0,0,0,.04)" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1C1B19", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {u.name || <span style={{ color: "#bbb", fontWeight: 400 }}>(geen naam)</span>}{isMe && <span style={{ fontSize: 11, color: "#999", fontWeight: 400 }}> (jij)</span>}
                </div>
                <div style={{ fontSize: 12, color: "#888", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                {(manageable && creator)
                  ? <select value={roleRank(u.role) >= 0 ? ROLES[roleRank(u.role)].key : u.role} disabled={busy} onChange={function (e) { changeRole(u, e.target.value); }} style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: FF, fontWeight: 700, color: "#1C1B19", background: "#fff" }}>
                      {roleOptions.map(function (r) { return <option key={r.key} value={r.key}>{r.label}</option>; })}
                    </select>
                  : <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", padding: "4px 10px", borderRadius: 6, background: "#1A2744", color: "#fff" }}>{roleLabel(u.role)}</span>}
                {(manageable || isMe) && <button disabled={busy} onClick={function () { setEditName(u.name || ""); setMdl({ type: "name", user: u }); }} style={smallBtn} title="Naam aanpassen">✎</button>}
                {manageable && <button disabled={busy} onClick={function () { newLink(u); }} style={smallBtn} title="Nieuwe link om wachtwoord in te stellen">🔑 Link</button>}
                {manageable && <button disabled={busy} onClick={function () { setMdl({ type: "delete", user: u }); }} style={Object.assign({}, smallBtn, { color: "#E53E3E", borderColor: "#FEB2B2" })} title="Verwijderen">×</button>}
              </div>
            </div>
          );
  }

  // Voor de creator: accounts gegroepeerd per club, huidige club bovenaan.
  var groups = {};
  (users || []).forEach(function (u) { var k = u.clubId || "__none"; (groups[k] = groups[k] || []).push(u); });
  function clubName(cid) { return cid === "__none" ? "Zonder club" : (clubs[cid] || cid); }
  var groupKeys = Object.keys(groups).sort(function (a, b) {
    if (a === clubId) return -1;
    if (b === clubId) return 1;
    return clubName(a).localeCompare(clubName(b));
  });

  var smallBtn = { background: "none", border: "1px solid #ccc", borderRadius: 6, padding: "5px 10px", fontSize: 11, cursor: "pointer", color: "#555", fontFamily: FF, fontWeight: 700 };

  return (
    <div style={{ minHeight: "100vh", padding: "16px " + pd + "px 48px", fontFamily: FF, maxWidth: 820, margin: "0 auto", background: "#F5F3EE" }}>
      <BackBtn label="Terug" onClick={onBack} />
      <h1 style={{ fontFamily: BF, fontSize: mob ? 22 : 29, color: "#1C1B19", marginBottom: 2, fontWeight: 800 }}>Accounts</h1>
      <p style={{ fontSize: 13, color: "#888", marginBottom: 20 }}>{creator ? "Alle clubs" : (club.name || club.slug)}</p>

      {creator && requests.length > 0 && <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontFamily: BF, fontSize: 14, color: "#1C1B19", marginBottom: 8, display: "flex", alignItems: "center", gap: 8, fontWeight: 800 }}>
          Aanvragen <span style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, background: "#DD6B20", color: "#fff", borderRadius: 10, padding: "1px 8px", letterSpacing: 0 }}>{requests.length}</span>
        </h3>
        {reqErr && <p style={{ color: "#E53E3E", fontSize: 12, marginBottom: 8 }}>{reqErr}</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {requests.map(function (r) {
            return <RequestCard key={r.uid} req={r} mob={mob} busy={reqBusy === r.uid} onApprove={approve} onReject={function (req) { setMdl({ type: "reject", req: req }); }} />;
          })}
        </div>
      </div>}

      <form onSubmit={create} style={{ background: "#fff", borderRadius: 14, padding: mob ? 16 : 20, marginBottom: 20, boxShadow: "0 2px 16px rgba(0,0,0,.05)" }}>
        <h3 style={{ fontFamily: BF, fontSize: 14, color: "#1C1B19", marginBottom: 12, fontWeight: 800 }}>Nieuw account</h3>
        <div style={{ display: "flex", gap: 10, flexDirection: mob ? "column" : "row" }}>
          <input value={name} onChange={function (e) { setName(e.target.value); }} placeholder="Naam" style={inputStyle} />
          <input type="email" value={email} onChange={function (e) { setEmail(e.target.value); }} placeholder="E-mailadres" required style={inputStyle} />
        </div>
        {creator && Object.keys(clubs).length > 0 && <div>
          <p style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>Club:</p>
          <select value={newClub} onChange={function (e) { setNewClub(e.target.value); }} style={Object.assign({}, inputStyle, { fontWeight: 700, color: "#1C1B19" })}>
            {Object.keys(clubs).sort(function (a, b) { return clubs[a].localeCompare(clubs[b]); }).map(function (id) { return <option key={id} value={id}>{clubs[id]}</option>; })}
          </select>
        </div>}
        {creator ? <div>
          <p style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>Rol:</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
            {roleOptions.map(function (r) {
              var active = role === r.key;
              return (
                <label key={r.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, border: active ? "2px solid #1A2744" : "1px solid #E2DED5", cursor: "pointer", background: active ? "#F7F8FB" : "#fff" }}>
                  <input type="radio" name="role" checked={active} onChange={function () { setRole(r.key); }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#1C1B19", minWidth: 96 }}>{r.label}</span>
                  <span style={{ fontSize: 12, color: "#888" }}>{r.desc}</span>
                </label>
              );
            })}
          </div>
        </div> : <p style={{ fontSize: 12, color: "#888", marginBottom: 14 }}>Het nieuwe account krijgt de rol <b style={{ color: "#1C1B19" }}>Trainer</b>.</p>}
        {createErr && <p style={{ color: "#E53E3E", fontSize: 12, marginBottom: 8 }}>{createErr}</p>}
        <button type="submit" disabled={creating || !email.trim()} style={{ width: "100%", padding: 11, borderRadius: 8, background: email.trim() ? "#1A2744" : "#ccc", color: "#fff", border: "none", cursor: creating ? "default" : "pointer", fontSize: 14, fontWeight: 700, opacity: creating ? .7 : 1 }}>{creating ? "Bezig..." : "Account aanmaken en uitnodiging sturen"}</button>
      </form>

      {rowErr && <p style={{ color: "#E53E3E", fontSize: 12, marginBottom: 10 }}>{rowErr}</p>}
      {loadErr && <p style={{ color: "#E53E3E", fontSize: 12, marginBottom: 10 }}>{loadErr}</p>}
      {users === null && <p style={{ color: "#999", fontSize: 13 }}>Laden...</p>}

      {creator ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {groupKeys.map(function (cid) {
            var list = groups[cid];
            var isOpen = !!openClubs[cid];
            return (
              <div key={cid}>
                <div role="button" tabIndex={0} onClick={function () { setOpenClubs(function (o) { var n = Object.assign({}, o); n[cid] = !o[cid]; return n; }); }} style={{ boxSizing: "border-box", width: "100%", display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "2px solid #E2DED5", borderRadius: 12, padding: "12px 16px", cursor: "pointer", fontFamily: FF, textAlign: "left" }}>
                  <span style={{ fontSize: 22 }}>{isOpen ? "📂" : "📁"}</span>
                  <span style={{ flex: 1, fontFamily: BF, fontSize: 14, color: "#1C1B19", fontWeight: 800 }}>{clubName(cid)}</span>
                  <span style={{ fontSize: 12, color: "#888" }}>{list.length} {list.length === 1 ? "account" : "accounts"}</span>
                  {cid !== clubId && clubSlugs[cid] && <a href={"/" + clubSlugs[cid]} onClick={function (e) { e.stopPropagation(); }} style={{ fontSize: 11, fontWeight: 700, color: "#1C1B19", border: "1px solid #ccc", borderRadius: 6, padding: "3px 8px", textDecoration: "none" }}>Bekijk club →</a>}
                  <span style={{ fontSize: 11, color: "#999" }}>{isOpen ? "▲" : "▼"}</span>
                </div>
                {isOpen && <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8, paddingLeft: mob ? 8 : 20 }}>
                  {list.map(renderRow)}
                </div>}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(users || []).map(renderRow)}
        </div>
      )}

      <div style={{ marginTop: 24, background: "#fff", borderRadius: 12, padding: "14px 16px" }}>
        <b style={{ fontFamily: BF, fontSize: 13, color: "#1C1B19", display: "block", marginBottom: 10, fontWeight: 800 }}>Rollen</b>
        <RolesList />
        {!creator && <p style={{ fontSize: 11, color: "#999", marginTop: 8 }}>Je kunt alleen accounts met de rol trainer aanmaken en beheren. Voor andere rollen: neem contact op met de creator.</p>}
      </div>
      {renderModal()}
    </div>
  );
}
